<template>
    <section
        id="join-physical"
        class="join-physical"
    >
        <v-row
            class="input-section"
            noGutters
        >
            <v-text-field
                v-model="barcode"
                class="input"
                :placeholder="( hasActiveShelf ? 'Scan a Product' : 'Scan a Shelf' )"
                dense
                outlined
                hideDetails
                autofocus
                @keydown.enter="scanHandler()"
            >
            </v-text-field>
            <ShelfHeading
                class="join-physical__shelf-title"
                :locator="activeShelf"
                align="inherit"
            />
        </v-row>
        <v-row noGutters>
            <h3 v-if="shelfItems.length">
                Scanned {{ scannedItems.length }} of {{ shelfItems.length }}
            </h3>
            <ClTableSimple
                :loading="inFlight"
                :items="shelfItemsDisplay"
                :headers="headers"
                itemKey="displayRef"
                class="join-physical__table"
            >
                <template #[`item.physicalInventoryScanned`]="{ item }">
                    <v-icon>{{ item.physicalInventoryScanned ? 'mdi-checkbox-marked-outline': 'mdi-checkbox-blank-outline' }}</v-icon>
                </template>
            </ClTableSimple>
        </v-row>
    </section>
</template>
<script>
import { mapState, mapGetters } from 'vuex';

// Components
import ShelfHeading from '@/views/shared/ShelfHeading';

// utils
import ErrorUtil from '@/utils/ErrorUtil';
import ScanUtil from '@/utils/ScanUtil';
import AudioUtil, { soundNames } from '@/utils/AudioUtil';

export default {
    name: 'JoinPhysical',
    components: {
        ShelfHeading,
    },
    data() {
        return {
            barcode: '',
            headers: [
                {
                    text: '',
                    value: 'physicalInventoryScanned',
                },
                {
                    text: 'Item ID',
                    align: 'start',
                    value: 'displayRef',
                },
                {
                    text: 'Title',
                    align: 'start',
                    value: 'titleDescription',
                },
            ],
        };
    },
    computed: {
        ...mapState( {
            activeShelf: ( state ) => state.Inventory.JoinPhysical.activeShelf,
            shelfItems: ( state ) => state.Inventory.JoinPhysical.shelfItems,
        } ),
        ...mapGetters( {
            hasActiveShelf: 'Inventory/JoinPhysical/hasActiveShelf',
            scannedItems: 'Inventory/JoinPhysical/scannedItems',
            inFlight: 'Inventory/JoinPhysical/inFlight',
        } ),
        /** @returns { Boolean } */
        isItemScan() {
            return ScanUtil.isSerialRef( this.barcode );
        },
        /** @returns { String } */
        processedBarcode() {
            return this.isItemScan
                ? this.barcode.substring( 2 )
                : this.barcode;
        },
        shelfItemsDisplay() {
            return this.shelfItems.map( ( item ) => ({
                ...item,
                displayRef: `ID${item.ref}`,
            }));
        },
        /** @returns { Boolean } */
        hasItemBeenScanned() {
            if ( !this.isItemScan ) {
                return false;
            }
            const filteredItems = this.shelfItems.filter( ( item ) => (typeof item.ref === 'undefined' ? '' : item.ref.toString() === this.processedBarcode));
            return filteredItems.length > 0
                && filteredItems[ 0 ].physicalInventoryScanned;
        },
    },
    beforeDestroy() {
        this.$store.dispatch( 'Inventory/JoinPhysical/reset' );
    },
    methods: {
        async scanHandler() {
            if ( !this.barcode ) {
                return;
            }
            if ( !this.isItemScan ) {
                await this.getShelfInfo();
            } else if ( !this.hasActiveShelf ) {
                this.$store.dispatch( 'snackbar', {
                    message: 'Scan a shelf to begin',
                    type: 'error',
                    duration: 5000,
                } );
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
            } else {
                await this.scanItem();
            }
            this.barcode = '';
        },
        async getShelfInfo() {
            try {
                await Promise.all(
                    [
                        this.$store.dispatch(
                            'Inventory/JoinPhysical/getShelfItemsByBarcode',
                            { barcode: this.barcode },
                        ),
                        this.$store.dispatch(
                            'Inventory/JoinPhysical/getShelfByBarcode',
                            { barcode: this.barcode },
                        ),
                    ],
                );
                await AudioUtil.playSound( soundNames.SUCCESS_LONG );
            } catch ( error ) {
                this.$store.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                } );
                await AudioUtil.playSound( soundNames.FAILURE_LONG );
            }
        },
        async scanItem() {
            if ( this.hasItemBeenScanned ) {
                await this.$store.dispatch( 'snackbar', {
                    message: `Serial item ID${ this.processedBarcode } already in the cart`,
                    type: 'error',
                    duration: 5000,
                } );
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                return;
            }
            try {
                const shelfItem = await this.$store.dispatch(
                    'Inventory/JoinPhysical/updateSerialItemLocator',
                    {
                        barcode: this.processedBarcode,
                        locatorName: this.activeShelf.name,
                        locatorRef: this.activeShelf.ref,
                    },
                );
                await this.$store.dispatch(
                    'Inventory/JoinPhysical/markShelfItemScanned',
                    {
                        shelfItem,
                    },
                );
                await AudioUtil.playSound( soundNames.SUCCESS_SHORT );
            } catch ( error ) {
                this.$store.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                } );
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
            }
        },
    },
};
</script>
<style lang="scss">
.join-physical {
    margin: 2rem 0;
    &__table {
        border: 1px solid $colors-grey;
        width: 100%;
    }

    & &__shelf-title {
        margin-bottom: 1rem;
        flex-grow: 1;

        text-align: start;
        @include mixins-above(tablet) {
            text-align: end;
            margin: 0;
        }
    }
    .input-section {
        display: flex;
        flex-direction: column-reverse;
        margin-bottom: 1rem;
        @include mixins-above(tablet) {
            flex-direction: row;
            margin-bottom: 2rem;
        }
        &__input {
            flex-grow: 1;
        }
        flex-wrap: wrap;
        justify-content: space-between;
    }
}
</style>
